/**
 * 피벗 한 장을 **어떤 그림으로 그릴 것인가** — 종류마다 필요한 축이 다르다.
 *
 * ⚠️ 열 축이 필요한 종류(누적 막대·히트맵)는 열을 안 골랐으면 화면이 그렇게 알린다 — 억지로 한 칸으로 그리면 거짓말이 된다.
 * ⚠️ 원형(도넛·트리맵)은 **행 합계 하나만** 쓴다 — 열이 있어도 접어서 그린다.
 */

export type AnalyticsChartKind = {
  key: string;
  label: string;
  icon: string;
  /** apexcharts 종류 */
  type: 'bar' | 'line' | 'area' | 'donut' | 'heatmap' | 'treemap';
  /** 가로 막대인가 */
  horizontal?: boolean;
  /** 계열을 쌓나 */
  stacked?: boolean;
  /** 행 합계 하나만 쓰는가(원형·트리맵) */
  totalsOnly?: boolean;
  /** 열 축이 있어야 뜻이 있는가 */
  needsColumn?: boolean;
  /**
   * 그림이 아니라 **순위 목록**인가 — 값이 큰 차례로 줄을 세운다.
   * 🔑 「계정 리스트」·「컨텐츠 유형 리스트」가 이것이다 — 축만 바꾸면 무엇이든 목록이 된다.
   * ⚠️ 행 합계 하나만 쓴다(`totalsOnly` 와 같은 성질) — 열 축이 있어도 합쳐 센다.
   */
  ranking?: boolean;
  /**
   * **막대와 선을 섞나** — 첫 계열은 막대, 나머지는 선.
   * 🔑 크기가 다른 두 이야기를 한 판에 놓을 때 쓴다(「건수는 막대, 조회수는 선」).
   * ⚠️ 상류는 `chart.type` 이 `line` 일 때만 계열마다 종류를 읽는다 — 그래서 `type` 이 `line` 이다.
   */
  mixed?: boolean;
  /**
   * **낱말 구름인가** — 값이 큰 낱말을 크게 적는다.
   * 🔑 해시태그·브랜드처럼 «무엇이 많이 나왔나»만 보면 될 때, 줄을 세우는 것보다 한눈에 들어온다.
   * ⚠️ 행 합계 하나만 쓴다(`totalsOnly` 와 같은 성질) · **순서가 뜻이 아니다** — 크기가 뜻이다.
   */
  cloud?: boolean;
  /**
   * **글 그 자체를 세우나** — 집계가 아니라 글 카드를 세운다.
   * 🚨 이것만 **축을 안 본다** — 지표로 차례만 정한다(행 축이 무엇이든 글은 글이다).
   * 🔑 숫자가 왜 그런지는 결국 글에서 본다 — 그 자리를 대시보드 안에 둔다.
   */
  posts?: boolean;
};

export const ANALYTICS_CHARTS: AnalyticsChartKind[] = [
  { key: 'column', label: '세로 막대', icon: 'solar:chart-2-linear', type: 'bar' },
  { key: 'stacked', label: '누적 막대', icon: 'solar:chart-square-linear', type: 'bar', stacked: true, needsColumn: true },
  { key: 'hbar', label: '가로 막대', icon: 'solar:graph-linear', type: 'bar', horizontal: true },
  { key: 'line', label: '선', icon: 'solar:chart-linear', type: 'line', needsColumn: true },
  { key: 'area', label: '영역', icon: 'solar:graph-linear', type: 'area', stacked: true, needsColumn: true },
  { key: 'donut', label: '도넛', icon: 'solar:pie-chart-linear', type: 'donut', totalsOnly: true },
  { key: 'treemap', label: '트리맵', icon: 'solar:widget-4-linear', type: 'treemap', totalsOnly: true },
  { key: 'heatmap', label: '히트맵', icon: 'solar:widget-4-linear', type: 'heatmap', needsColumn: true },
  { key: 'mixed', label: '혼합(막대+선)', icon: 'solar:chart-2-linear', type: 'line', needsColumn: true, mixed: true },
  { key: 'posts', label: '글 목록', icon: 'solar:documents-linear', type: 'bar', totalsOnly: true, posts: true },
  /*
    🔑 **목록도 그림 종류 하나로 둔다** — 담고 빼고 저장하는 길이 차트와 똑같아야, 대시보드가
      「차트 판」과 「목록 판」 둘로 갈리지 않는다.
  */
  { key: 'ranking', label: '순위 목록', icon: 'solar:list-linear', type: 'bar', totalsOnly: true, ranking: true },
  { key: 'cloud', label: '낱말 구름', icon: 'solar:hashtag-linear', type: 'bar', totalsOnly: true, cloud: true },
];

export function findChartKind(key: string): AnalyticsChartKind {
  return ANALYTICS_CHARTS.find((chart) => chart.key === key) ?? ANALYTICS_CHARTS[0];
}

/** 날짜 열이면 선·영역이 어울린다 — 화면이 처음 고르는 값 */
export const ANALYTICS_DEFAULT_CHART = 'column';
